import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthProvider';
import { ThemeProvider } from './contexts/ThemeProvider';
import { useAuth } from './contexts/useAuth';
import { useTheme } from './contexts/useTheme';

const AuthPage = lazy(() => import('./pages/AuthPage').then((module) => ({ default: module.AuthPage })));
const GoogleCallbackPage = lazy(() => import('./pages/GoogleCallbackPage').then((module) => ({ default: module.GoogleCallbackPage })));
const PublicUploadPage = lazy(() => import('./pages/PublicUploadPage').then((module) => ({ default: module.PublicUploadPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const DrivePage = lazy(() => import('./pages/DrivePage').then((module) => ({ default: module.DrivePage })));
const TrashPage = lazy(() => import('./pages/TrashPage').then((module) => ({ default: module.TrashPage })));

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 20_000 } } });

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <div className="grid min-h-dvh place-items-center [background:var(--bg)]"><div className="size-10 animate-pulse rounded-2xl [background:var(--accent)]" /></div>;
  return user ? <Outlet /> : <Navigate to="/login" replace />;
}

const router = createBrowserRouter([
  { path: '/login', element: <Suspense fallback={<PageLoader />}><AuthPage /></Suspense> },
  { path: '/auth/google/callback', element: <Suspense fallback={<PageLoader />}><GoogleCallbackPage /></Suspense> },
  { path: '/subir', element: <Suspense fallback={<PageLoader />}><PublicUploadPage /></Suspense> },
  { element: <Protected />, children: [
    { path: '/drive/:folderId?', element: <Suspense fallback={<PageLoader />}><DrivePage /></Suspense> },
    { path: '/trash', element: <Suspense fallback={<PageLoader />}><TrashPage /></Suspense> },
    { path: '/profile', element: <Suspense fallback={<PageLoader />}><ProfilePage /></Suspense> },
  ] },
  { path: '*', element: <Navigate to="/drive" replace /> },
]);

function PageLoader() {
  return <div className="grid min-h-dvh place-items-center [background:var(--bg)]"><div className="size-10 animate-pulse rounded-2xl [background:var(--accent)]" /></div>;
}

function Notifications() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-center" richColors closeButton />;
}

export default function App() {
  return <ThemeProvider><QueryClientProvider client={queryClient}><AuthProvider><RouterProvider router={router} /><Notifications /></AuthProvider></QueryClientProvider></ThemeProvider>;
}
